"use client"

import { useState } from "react"
import mailcheck from "mailcheck"
import { ArrowRight, Check } from "lucide-react"
import {
  MATCH_AGE_MAX,
  MATCH_AGE_MIN,
  parseAgeRange,
  type ApplicationQuestion,
  type ApplyProductCard,
} from "@/lib/coaching/application"
import { cn } from "@/lib/utils"

export function QuestionInput({
  q,
  value,
  onChange,
  onChoose,
  onEnter,
  errorId,
}: {
  q: ApplicationQuestion
  value: string
  onChange: (value: string) => void
  onChoose: (value: string) => void
  onEnter: () => void
  errorId?: string | null
}) {
  const fieldClass =
    "w-full border border-[var(--color-rule)] bg-[var(--color-paper)] px-4 py-3.5 text-[1.05rem] text-[var(--color-ink)] outline-none transition-colors placeholder:text-[var(--color-ink-soft)] focus:border-[var(--color-vermilion)] focus-visible:ring-2 focus-visible:ring-[var(--color-vermilion)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-paper)]"
  const describedBy =
    [q.help ? `q-${q.id}-help` : null, errorId]
      .filter((id): id is string => Boolean(id))
      .join(" ") || undefined

  if (q.type === "single") {
    return <SingleSelectField q={q} value={value} onChoose={onChoose} describedBy={describedBy} errorId={errorId} />
  }

  if (q.type === "multi") {
    return <MultiSelectField q={q} value={value} onChange={onChange} describedBy={describedBy} />
  }

  if (q.type === "agerange") {
    return <AgeRangeField q={q} value={value} onChange={onChange} describedBy={describedBy} />
  }

  if (q.type === "longtext") {
    return (
      <textarea
        id={`q-${q.id}`}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
            event.preventDefault()
            onEnter()
          }
        }}
        placeholder={q.placeholder}
        maxLength={q.maxLength}
        aria-required={q.required || undefined}
        aria-invalid={errorId ? true : undefined}
        aria-describedby={describedBy}
        rows={5}
        className={cn(fieldClass, "resize-y")}
      />
    )
  }

  const inputType =
    q.type === "email" ? "email" : q.type === "number" ? "number" : "text"

  return (
    <TextField
      q={q}
      inputType={inputType}
      value={value}
      onChange={onChange}
      onEnter={onEnter}
      fieldClass={fieldClass}
      describedBy={describedBy}
      errorId={errorId}
    />
  )
}

/** The plain text, email and number input. An email address gets the free
 *  typo catcher on blur (mailcheck: warn only, never block), so
 *  john@gmial.com is offered gmail.com and the submit is never held. */
function TextField({
  q,
  inputType,
  value,
  onChange,
  onEnter,
  fieldClass,
  describedBy,
  errorId,
}: {
  q: ApplicationQuestion
  inputType: "email" | "number" | "text"
  value: string
  onChange: (value: string) => void
  onEnter: () => void
  fieldClass: string
  describedBy?: string
  errorId?: string | null
}) {
  const [suggestion, setSuggestion] = useState<string | null>(null)
  const checkTypo = () => {
    if (q.type !== "email" || !value.includes("@")) {
      setSuggestion(null)
      return
    }
    mailcheck.run({
      email: value.trim(),
      suggested: (s: { full: string }) => setSuggestion(s.full),
      empty: () => setSuggestion(null),
    })
  }

  const suggestionId = suggestion ? `q-${q.id}-suggestion` : null
  const inputDescription =
    [describedBy, suggestionId].filter((id): id is string => Boolean(id)).join(" ") || undefined

  return (
    <>
    <input
      id={`q-${q.id}`}
      type={inputType}
      value={value}
      onChange={(event) => {
        onChange(event.target.value)
        if (suggestion) setSuggestion(null)
      }}
      onBlur={checkTypo}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          event.preventDefault()
          onEnter()
        }
      }}
      placeholder={q.placeholder}
      maxLength={q.maxLength}
      autoComplete={
        q.type === "email"
          ? "email"
          : q.id === "name"
            ? "name"
            : undefined
      }
      // Mobile keyboard quality: the action key advances the step, email
      // keyboards skip autocorrect/capitalization (they mangle addresses).
      enterKeyHint="next"
      inputMode={q.type === "email" ? "email" : q.type === "number" ? "numeric" : undefined}
      autoCapitalize={q.type === "email" ? "none" : q.id === "name" ? "words" : undefined}
      autoCorrect={q.type === "email" ? "off" : undefined}
      spellCheck={q.type === "email" ? false : undefined}
      aria-required={q.required || undefined}
      aria-invalid={errorId ? true : undefined}
      aria-describedby={inputDescription}
      className={fieldClass}
    />
    {suggestion ? (
      <p
        id={suggestionId ?? undefined}
        role="status"
        aria-live="polite"
        className="mt-2 text-[0.92rem] leading-[1.5] text-[var(--color-ink-muted)]"
      >
        Did you mean{" "}
        <button
          type="button"
          onClick={() => {
            onChange(suggestion)
            setSuggestion(null)
          }}
          className="font-semibold text-[var(--color-vermilion)] underline underline-offset-4"
        >
          {suggestion}
        </button>
        ?
      </p>
    ) : null}
    </>
  )
}

const OPTION_BUTTON_CLASS = {
  base: "flex min-h-11 w-full touch-manipulation items-center justify-between gap-3 border px-4 py-3.5 text-left text-[1.02rem] leading-[1.4] outline-none transition-colors focus-visible:ring-2 focus-visible:ring-[var(--color-vermilion)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-paper)]",
  selected:
    "border-[var(--color-vermilion)] bg-[var(--color-paper-high)] text-[var(--color-ink)] shadow-[var(--glass-shadow-2)]",
  unselected:
    "border-[var(--color-rule)] bg-[var(--color-paper)] text-[var(--color-ink-muted)] hover:border-[var(--color-rule-strong)] hover:text-[var(--color-ink)]",
}

/** The one product a product step points at: the answer-option box, linking
 * to the product's own page, with an arrow where an option shows its radio.
 * Everything inside it is the product's own copy and live price. */
export function ProductCardLink({ card }: { card: ApplyProductCard }) {
  return (
    <a href={card.href} className={cn(OPTION_BUTTON_CLASS.base, OPTION_BUTTON_CLASS.unselected, "no-underline")}>
      <span className="flex flex-col gap-1">
        <span className="font-semibold text-[var(--color-ink)]">{card.title}</span>
        <span className="text-[0.95rem] leading-[1.5] text-[var(--color-ink-muted)]">{card.subtitle}</span>
        <span className="font-semibold text-[var(--color-ink)]">{card.price}</span>
      </span>
      <ArrowRight size={18} className="shrink-0 text-[var(--color-vermilion)]" aria-hidden />
    </a>
  )
}

/** One radio-style option button. */
function OptionButton({
  label,
  selected,
  onClick,
}: {
  label: string
  selected: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onClick}
      className={cn(
        OPTION_BUTTON_CLASS.base,
        selected ? OPTION_BUTTON_CLASS.selected : OPTION_BUTTON_CLASS.unselected,
      )}
    >
      <span>{label}</span>
      <span
        className={cn(
          "flex h-5 w-5 shrink-0 items-center justify-center rounded-full border transition-colors",
          selected
            ? "border-[var(--color-vermilion)] bg-[var(--color-vermilion)] text-[var(--color-paper)]"
            : "border-[var(--color-rule-strong)]",
        )}
        aria-hidden
      >
        {selected ? <Check size={13} /> : null}
      </span>
    </button>
  )
}

/** Single-select radiogroup. Every option is a one-tap choice that
 * auto-advances via onChoose. The "Something else" write-in was removed
 * (owner, 2026-07-17: every question is pure multiple choice). */
function SingleSelectField({
  q,
  value,
  onChoose,
  describedBy,
  errorId,
}: {
  q: ApplicationQuestion
  value: string
  onChoose: (value: string) => void
  describedBy?: string
  errorId?: string | null
}) {
  const options = q.options ?? []

  return (
    <div
      id={`q-${q.id}`}
      role="radiogroup"
      aria-label={q.label}
      aria-required={q.required || undefined}
      aria-invalid={errorId ? true : undefined}
      aria-describedby={describedBy}
      className="grid gap-2.5"
    >
      {options.map((option) => (
        <OptionButton
          key={option.value}
          label={option.label}
          selected={value === option.value}
          onClick={() => onChoose(option.value)}
        />
      ))}
    </div>
  )
}

/**
 * Two coupled age sliders, stored as "min-max". Only the Done-For-You branch
 * asks this, and only because we do the swiping: his age range is an
 * operational input for the people running his apps, not a profile question.
 *
 * The sliders show a real range before he touches anything, so an untouched
 * control still records that range (see withStepDefaults). Each handle clamps
 * against the other, which is why one value can never cross the other and the
 * stored range is always valid.
 */
function AgeRangeField({
  q,
  value,
  onChange,
  describedBy,
}: {
  q: ApplicationQuestion
  value: string
  onChange: (value: string) => void
  describedBy?: string
}) {
  const range =
    parseAgeRange(value) ??
    parseAgeRange(q.defaultValue) ?? { min: MATCH_AGE_MIN, max: MATCH_AGE_MAX }

  function commit(min: number, max: number) {
    onChange(`${min}-${max}`)
  }

  const rows: { key: "min" | "max"; label: string; current: number }[] = [
    { key: "min", label: "Youngest", current: range.min },
    { key: "max", label: "Oldest", current: range.max },
  ]

  return (
    <div id={`q-${q.id}`} role="group" aria-label={q.label} aria-describedby={describedBy} className="grid gap-4">
      {rows.map((row) => (
        <div key={row.key}>
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-[0.8rem] font-semibold uppercase tracking-[0.12em] text-[var(--color-ink-soft)]">
              {row.label}
            </span>
            <span className="text-[1.05rem] font-semibold text-[var(--color-ink)]">
              {row.current}
            </span>
          </div>
          <input
            type="range"
            min={MATCH_AGE_MIN}
            max={MATCH_AGE_MAX}
            step={1}
            value={row.current}
            aria-label={`${row.label} age`}
            onChange={(event) => {
              const next = Number(event.target.value)
              if (!Number.isFinite(next)) return
              if (row.key === "min") commit(Math.min(next, range.max), range.max)
              else commit(range.min, Math.max(next, range.min))
            }}
            // The one crisp solid accent; no tint, no wash.
            style={{ accentColor: "var(--color-vermilion)" }}
            className="mt-2 h-11 w-full touch-manipulation bg-transparent"
          />
        </div>
      ))}
    </div>
  )
}

/** Multi-select: pick any number of options (owner, 2026-07-18: "what you've
 * tried" and "your goal" are multiple-select). Each tap TOGGLES a checkbox and
 * NEVER auto-advances - the applicant picks several, then presses Continue.
 * The answer is stored as a comma-joined string of option values (via onChange,
 * not onChoose), so it stays a plain string in the payload/email/validation. */
function MultiSelectField({
  q,
  value,
  onChange,
  describedBy,
}: {
  q: ApplicationQuestion
  value: string
  onChange: (value: string) => void
  describedBy?: string
}) {
  const options = q.options ?? []
  const selected = value
    .split(",")
    .map((v) => v.trim())
    .filter(Boolean)

  function toggle(optionValue: string) {
    const next = selected.includes(optionValue)
      ? selected.filter((v) => v !== optionValue)
      // Preserve option order so the stored value + team-email read left-to-right.
      : options.map((o) => o.value).filter((v) => v === optionValue || selected.includes(v))
    onChange(next.join(","))
  }

  const requiredId = q.required ? `q-${q.id}-required` : null
  const groupDescription =
    [describedBy, requiredId].filter((id): id is string => Boolean(id)).join(" ") || undefined

  return (
    <>
      <div
        id={`q-${q.id}`}
        role="group"
        aria-label={q.label}
        aria-describedby={groupDescription}
        className="grid gap-2.5"
      >
        {options.map((option) => {
          const isOn = selected.includes(option.value)
          return (
            <button
              key={option.value}
              type="button"
              role="checkbox"
              aria-checked={isOn}
              onClick={() => toggle(option.value)}
              className={cn(
                OPTION_BUTTON_CLASS.base,
                isOn ? OPTION_BUTTON_CLASS.selected : OPTION_BUTTON_CLASS.unselected,
              )}
            >
              <span>{option.label}</span>
              <span
                className={cn(
                  "flex h-5 w-5 flex-none items-center justify-center rounded-[4px] border",
                  isOn
                    ? "border-[var(--color-vermilion)] bg-[var(--color-vermilion)] text-[var(--color-paper)]"
                    : "border-[var(--color-rule-strong)]",
                )}
                aria-hidden
              >
                {isOn ? <Check size={13} /> : null}
              </span>
            </button>
          )
        })}
      </div>
      {requiredId ? (
        <span id={requiredId} className="sr-only">
          Required
        </span>
      ) : null}
    </>
  )
}
